import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Sidebar } from './Sidebar';

export const Header = ({ className }) => {
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const notifications = [
    { id: 1, title: 'BTC Price Alert', message: 'Bitcoin crossed $67,000', time: '2m ago' },
    { id: 2, title: 'Trade Executed', message: 'Bought 0.5 ETH at $3,245', time: '15m ago' },
    { id: 3, title: 'Market Update', message: 'Market cap reaches $2.5T', time: '1h ago' },
  ];

  return (
    <header 
      className={cn(
        "h-14 border-b border-border bg-card flex items-center justify-between px-4 lg:px-6 shrink-0",
        className
      )}
    >
      {/* Left Side - Mobile Menu + Search */}
      <div className="flex items-center gap-3 flex-1">
        {/* Mobile Menu Toggle */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden shrink-0">
              <i className="fa-solid fa-bars text-base"></i>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-60">
            <Sidebar onClose={() => setMobileMenuOpen(false)} />
          </SheetContent>
        </Sheet>

        {/* Search - Hidden on mobile */}
        <div className="relative hidden sm:block max-w-xs flex-1">
          <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm"></i>
          <Input
            type="search"
            placeholder={t('search')}
            className="pl-9 h-9 bg-background"
          />
        </div>
      </div>

      {/* Right Side - Actions */}
      <div className="flex items-center gap-1 sm:gap-2">
        {/* Mobile Search */}
        <Button variant="ghost" size="icon" className="sm:hidden">
          <i className="fa-solid fa-magnifying-glass text-base"></i>
        </Button>

        {/* Language Toggle */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="language-toggle">
              <span className="text-sm font-medium">{language.toUpperCase()}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem 
              onClick={() => setLanguage('en')}
              className={cn(language === 'en' && "bg-primary/10")}
            >
              <span className="mr-2">🇬🇧</span>
              English
              {language === 'en' && <i className="fa-solid fa-check ml-auto text-primary"></i>}
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setLanguage('de')}
              className={cn(language === 'de' && "bg-primary/10")}
            >
              <span className="mr-2">🇩🇪</span>
              Deutsch
              {language === 'de' && <i className="fa-solid fa-check ml-auto text-primary"></i>}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative">
              <i className="fa-solid fa-bell text-base"></i>
              <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-destructive" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>{t('notifications')}</span>
              <Badge variant="secondary" className="text-xs">3 {t('new')}</Badge>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {notifications.map((notification) => (
              <DropdownMenuItem key={notification.id} className="flex flex-col items-start gap-1 p-3 cursor-pointer">
                <div className="flex items-center justify-between w-full">
                  <span className="font-medium text-sm">{notification.title}</span>
                  <span className="text-xs text-muted-foreground">{notification.time}</span>
                </div>
                <span className="text-xs text-muted-foreground">{notification.message}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-center text-primary text-sm justify-center cursor-pointer"
              onClick={() => navigate('/alerts')}
            >
              {t('viewAll')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          data-testid="theme-toggle"
        >
          {theme === 'dark' ? (
            <i className="fa-solid fa-circle-half-stroke text-base"></i>
          ) : (
            <i className="fa-solid fa-moon text-base"></i>
          )}
        </Button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full">
              <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                <span className="text-secondary-foreground font-medium text-sm">TL</span>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span>Tim Luczak</span>
                <span className="text-xs text-muted-foreground font-normal">info@tim-luczak.de</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
              <i className="fa-solid fa-user mr-2 text-sm w-4"></i>
              {t('profile')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
              <i className="fa-solid fa-gear mr-2 text-sm w-4"></i>
              {t('settings')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive cursor-pointer">
              <i className="fa-solid fa-right-from-bracket mr-2 text-sm w-4"></i>
              {t('logOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;
